import React, {
  useContext,
  useState,
  useEffect,
} from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Builder } from '@builder.io/react/lite';
import { useRouter } from 'next/router';
import useBuilderUpdater from '@glowforge/builder-components/lib/util/useBuilderUpdater';
import {
  MagicSvgContext,
  MagicSvgDispatchContext,
} from '../../../context/MagicSvgContext/MagicSvgContext';
import {
  STATUS,
  PAGES,
  magicSvgActions,
} from '../../../context/MagicSvgContext/magicSvgReducer';
import { postDelivery, deleteImage } from '../../../api/magic-svg';
import { USER_ACTIONS, IMAGE_STATUS } from '../constants';

import {
  GeneratedImageColumn,
  GeneratedImageContainer,
  GeneratedImageSectionContainer,
  ResultPageContainer,
  ShareButtonsContainer,
} from '../magic-svg.styles';
import MagicSvgShare from '../magic-svg-share';
import MagicSvgGeneratedImage from '../magic-svg-generated-image';
import MagicSvgActionButtons from '../magic-svg-action-buttons';

export default function Result() {
  const {
    image,
    generationStatus,
    recentImages,
  } = useContext(MagicSvgContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { updateContentData } = useBuilderUpdater();
  const { onGenerate, dispatch } = useContext(MagicSvgDispatchContext);
  const [currentView, setCurrentView] = useState(
    generationStatus === STATUS.SUCCESS ? USER_ACTIONS.REGENERATE : USER_ACTIONS.SCRAP
  );
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [hideDownload, setHideDownload] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (!generationStatus && !Builder.isEditing) {
      router.push({
        query: {
          ...router.query,
          page: PAGES.GENERATE,
          shallow: true,
        }
      });
    }
  }, [image, generationStatus, router]);

  const handleRegenerate = () => {
    const { prompt, artStyle, additionalFields } = image;
    onGenerate(prompt, artStyle, additionalFields);
    dispatch({
      type: magicSvgActions.SET_SHOW_BANNER,
      showBanner: false,
    });
    setCurrentView(USER_ACTIONS.SCRAP);
  };

  const handleDelivery = async () => {
    try {
      setIsPerformingAction(true);
      const { image: updatedImage, tokens: updatedTokens } = await postDelivery(image.id);
      dispatch({
        type: magicSvgActions.SET_USER_TOKEN,
        userToken: updatedTokens,
      });

      dispatch({
        type: magicSvgActions.SET_SHOW_BANNER,
        showBanner: true,
        userAction: USER_ACTIONS.DELIVER,
      });

      dispatch({
        type: magicSvgActions.SET_RECENT_IMAGES,
        recentImages: [updatedImage, ...recentImages],
      });

      // Update the URL with the result ID
      router.replace({
        query: {
          ...router.query,
          result: image.id,
        }
      }, undefined, { shallow: true });

      setCurrentView(USER_ACTIONS.REGENERATE);
      setHideDownload(true);
      updateContentData({
        image: {
          imageFile: updatedImage.previewUrl || updatedImage.imageUrl,
          width: 1200,
          height: 630,
        }
      });
    } catch (error) {
      if (error.message.includes('402')) {
        dispatch({
          type: magicSvgActions.REGISTER_INSUFFICIENT_TOKEN_ATTEMPT,
        });
      } else {
        router.push({
          query: {
            ...router.query,
            page: PAGES.LANDING,
            shallow: true,
          }
        });
        console.error('Error downloading the SVG:', error);
      }
    } finally {
      setIsPerformingAction(false);
    }
  };

  const handleStartNew = () => {
    router.push({
      query: {
        ...router.query,
        page: PAGES.GENERATE,
        shallow: true,
      }
    });
  };

  const handleScrap = async () => {
    try {
      setIsPerformingAction(true);
      deleteImage(image.id).then(updatedTokens => {
        dispatch({
          type: magicSvgActions.SET_USER_TOKEN,
          userToken: updatedTokens,
        });
      });

      const updatedRecentImages = recentImages.filter(recentImage => recentImage.id !== image.id);
      dispatch({
        type: magicSvgActions.SET_RECENT_IMAGES,
        recentImages: updatedRecentImages,
      });

      router.push({
        query: {
          ...router.query,
          page: PAGES.GENERATE,
          shallow: true,
        }
      });

      dispatch({
        type: magicSvgActions.SET_SHOW_BANNER,
        showBanner: true,
        userAction: USER_ACTIONS.SCRAP,
      });
    } catch (error) {
      console.error('Error deleting design', error);
      router.push({
        query: {
          ...router.query,
          page: PAGES.GENERATE,
          shallow: true,
        }
      });
    }
  };

  return (
    <ResultPageContainer container>
      <GeneratedImageSectionContainer container item>
        <GeneratedImageColumn>
          <Typography variant='subtitle1' textAlign='center' paddingBottom='10px'>
            {image.prompt}
          </Typography>
          <GeneratedImageContainer>
            <MagicSvgGeneratedImage
              currentView={currentView}
              isMobile={isMobile}
              hideDownload={hideDownload}
            />
          </GeneratedImageContainer>
        </GeneratedImageColumn>
      </GeneratedImageSectionContainer>
      {isMobile
        && (currentView === USER_ACTIONS.REGENERATE || image.status === IMAGE_STATUS.DELIVERED)
        && (
          <ShareButtonsContainer>
            <MagicSvgShare isMobile={isMobile} image={image} hideDownload={hideDownload} />
          </ShareButtonsContainer>
        )}
      <MagicSvgActionButtons
        currentView={currentView}
        isPerformingAction={isPerformingAction}
        handleRegenerate={handleRegenerate}
        handleStartNew={handleStartNew}
        handleDelivery={handleDelivery}
        handleScrap={handleScrap}
      />
    </ResultPageContainer>
  );
};
